// src/pages/Marketplace/markets/MarketMapPreview.tsx
import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_TOKEN } from '@/config/private/mapboxConfig';

// Project convention: token is centralized in `@/config/private/mapboxConfig`
// (which reads `REACT_APP_MAPBOX_TOKEN`). We reuse it here instead of reading
// the env var directly so the entire app has a single source of truth.
mapboxgl.accessToken = MAPBOX_TOKEN || mapboxgl.accessToken;

interface Props {
  lat: number | null;
  lng: number | null;
  radiusKm: number | null;
}

export default function MarketMapPreview({ lat, lng, radiusKm }: Props): React.ReactElement {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!container.current || lat == null || lng == null) return;
    const map = new mapboxgl.Map({
      container: container.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [lng, lat],
      zoom: 11,
    });
    mapRef.current = map;

    map.on('load', () => {
      if (radiusKm != null) {
        map.addSource('radius', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lng, lat] },
            properties: {},
          },
        });
        map.addLayer({
          id: 'radius-circle',
          type: 'circle',
          source: 'radius',
          paint: {
            'circle-radius': {
              stops: [[0, 0], [20, metersToPixels(radiusKm * 1000, lat, 20)]],
              base: 2,
            },
            'circle-color': '#7c3aed',
            'circle-opacity': 0.2,
            'circle-stroke-color': '#a78bfa',
            'circle-stroke-width': 1,
          },
        });
      }
      new mapboxgl.Marker({ color: '#a78bfa' }).setLngLat([lng, lat]).addTo(map);
    });

    return () => { map.remove(); mapRef.current = null; };
  }, [lat, lng, radiusKm]);

  if (lat == null || lng == null) {
    return <div className="text-sm text-gray-500">Enter coordinates to see the map preview.</div>;
  }
  return <div ref={container} className="w-full h-[300px] rounded-lg border border-gray-800" />;
}

function metersToPixels(meters: number, lat: number, zoom: number): number {
  const metersPerPixel = (40075016.686 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom + 8);
  return meters / metersPerPixel;
}
